import React, { useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const UomsTable = ({
  uoms,
  onSort,
  onRowClick,
  onPaginationChanged,
  paginationState,
}) => {
  const { t } = useTranslation(["markets"]);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: t("Name"),
        sortable: true,
        filter: true,
        flex: 2,
        minWidth: 150,
        tooltipValueGetter: (params) => params.value,
        headerTooltip: t("Name"),
      },
      {
        field: "code",
        headerName: t("UomCode"),
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
        tooltipValueGetter: (params) => params.value,
        headerTooltip: t("UomCode"),
      },
      {
        field: "uomTypeName",
        headerName: t("UomType"),
        sortable: true,
        filter: true,
        flex: 1.5,
        minWidth: 140,
        tooltipValueGetter: (params) => params.value,
        headerTooltip: t("UomType"),
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        flex: 1,
        headerClass: "ag-center-aligned-header",
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
        headerTooltip: t("Active"),
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  // Handle sort changes to maintain consistent sorting API
  const handleSortChanged = (event) => {
    if (!onSort) return;
    const sortModel = event.api.getSortModel();
    if (sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      onSort(colId, sort);
    } else {
      onSort(null, null);
    }
  };

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pageSize: paginationState?.pageSize || 20,
      pageSizeOptions: [10, 20, 50, 100],
    }),
    [paginationState?.pageSize]
  );

  // Handle pagination changes
  const handlePaginationChanged = useCallback(
    (params) => {
      if (onPaginationChanged && params) {
        onPaginationChanged(params);
      }
    },
    [onPaginationChanged]
  );

  // Grid ready handler
  const onGridReady = useCallback(
    (params) => {
      if (paginationState && paginationState.currentPage >= 0) {
        setTimeout(() => {
          params.api.paginationGoToPage(paginationState.currentPage);
        }, 0);
      }
    },
    [paginationState]
  );

  return (
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={uoms}
      onSortChanged={onSort ? handleSortChanged : undefined}
      onRowClicked={(params) => onRowClick && onRowClick(params.data)}
      onPaginationChanged={handlePaginationChanged}
      onGridReady={onGridReady}
      getRowId={(params) => params.data.uomId.toString()}
      animateRows={true}
      domLayout="autoHeight"
      pagination={true}
      paginationPageSize={paginationConfig.pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      suppressPaginationPanel={false}
    />
  );
};

export default UomsTable;

